import type { AccountRegion, M49Area } from "@baton/api";
import type { I18n } from "@baton/i18n";
import { countryName } from "./countryName.ts";

/**
 * 020 FR-020-255, FR-020-262 (2026-09-28, the owner's decision): where a client is. A client is in a
 * country, or, when its country is not known, located by one UN M49 area, a region, a sub-region or an
 * intermediate region. Every shape that names a client carries `country`, null for a client located by
 * an area alone, and `region`, its place in the geoscheme, which for such a client runs from its region
 * down to the area it is located by and no further; so the area is the finest level `region` names.
 * A place is shown as the country's flag and name, or as the globe and the area's name. The area's
 * name and the region-only heading are the shell catalogue's, because the Clients sidebar, the Projects
 * branch and the task lists grouped by client name an area outside the account area's chunk.
 */
export interface Located {
  readonly country: string | null;
  readonly region: AccountRegion;
}

/** Where a client is, as a surface draws it: a country by its code, or the area it is located by. */
export type Place = { readonly kind: "country"; readonly code: string } | { readonly kind: "area"; readonly area: M49Area };

type Translate = I18n["t"];

/** The finest level a placement names: the intermediate region, else the sub-region, else the region. */
export function finestArea(region: AccountRegion): M49Area {
  return region.intermediate_region ?? region.sub_region ?? region.region;
}

export function placeOf(client: Located): Place {
  return client.country === null ? { kind: "area", area: finestArea(client.region) } : { kind: "country", code: client.country };
}

/** An area's name as the reader reads it, the UN's English name where the catalogue has none. */
export function areaName(t: Translate, area: M49Area): string {
  return t("account.area", { code: area.code, name: area.name });
}

/** A heading or a filter value of the clients located by an area alone: "Africa (region)". */
export function regionOnlyLabel(t: Translate, area: M49Area): string {
  return t("account.location.regionOnly", { area: areaName(t, area) });
}

/** A place's name: the country's in the reader's locale, or the area's. */
export function placeName(i18n: Pick<I18n, "t" | "locale">, place: Place): string {
  return place.kind === "country" ? countryName(i18n.locale, place.code) : areaName(i18n.t, place.area);
}

/** Where a client is, named: its country, or the area it is located by. */
export function locationName(i18n: Pick<I18n, "t" | "locale">, client: Located): string {
  return placeName(i18n, placeOf(client));
}

/** The members a create or a patch names the place by: `country`, or `region` for an area. */
export function locationBody(place: Place): { readonly country: string } | { readonly region: string } {
  return place.kind === "country" ? { country: place.code } : { region: place.area.code };
}

/** Whether two places are the same one. */
export function samePlace(a: Place, b: Place): boolean {
  return a.kind === "country" ? b.kind === "country" && a.code === b.code : b.kind === "area" && a.area.code === b.area.code;
}
